import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function FAQSection() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = Array.from({ length: 17 }, (_, i) => ({
    question: t(`faq.q${i + 1}`),
    answer: t(`faq.a${i + 1}`),
  }));

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-16 md:py-24 bg-background-100">
      <div className="mx-auto max-w-[1440px] px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
          {/* Left */}
          <div className="lg:w-[35%] flex-shrink-0">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
              {t('faq.title')}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              {t('faq.heading1')}
              <br />
              {t('faq.heading2')}
              <br />
              {t('faq.heading3')}
            </h2>
            <p className="text-sm text-foreground-600 max-w-xs">
              {t('faq.desc')}
            </p>
          </div>

          {/* Right - FAQ accordion */}
          <div className="lg:w-[65%]">
            <div className="bg-accent-600 rounded-[32px] p-6 md:p-10">
              <div className="space-y-2">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="border border-background-50/10 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full flex items-center justify-between gap-3 p-4 md:p-5 text-left cursor-pointer hover:bg-background-50/5 transition-colors"
                    >
                      <span className="text-sm md:text-base font-medium text-background-50/90 pr-4">
                        {faq.question}
                      </span>
                      <span className={`flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-45' : ''}`}>
                        <i className="ri-add-line text-background-50/60"></i>
                      </span>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        openIndex === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="px-4 md:px-5 pb-4 md:pb-5">
                        <p className="text-sm text-background-50/70 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Structured data for FAQ */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </section>
  );
}